import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NightModeContextType {
  isNightMode: boolean;
  toggleNightMode: () => void;
}

const NightModeContext = createContext<NightModeContextType | undefined>(undefined);

export const useNightMode = () => {
  const context = useContext(NightModeContext);
  if (context === undefined) {
    throw new Error('useNightMode must be used within a NightModeProvider');
  }
  return context;
};

interface NightModeProviderProps {
  children: ReactNode;
}

/**
 * Theme provider aligned with Downloads/eventox:
 * - Dark (night) is default — body has ex-theme, no light-theme
 * - Light — body has ex-theme + light-theme
 * Still toggles legacy html.night-mode for older CSS that depends on it.
 */
export const NightModeProvider: React.FC<NightModeProviderProps> = ({ children }) => {
  // Default dark to match the Eventox mock ("Eventox Original")
  const [isNightMode, setIsNightMode] = useState(true);

  // Apply body/html theme classes from saved preference
  const applyThemeClasses = (nightMode: boolean) => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    // Always mark the app as using the new theme layer
    bodyElement.classList.add('ex-theme');

    if (nightMode) {
      htmlElement.classList.add('night-mode');
      bodyElement.classList.remove('light-theme');
      localStorage.setItem('gmtNightMode', 'true');
      localStorage.setItem('eventox_theme', 'dark');
    } else {
      htmlElement.classList.remove('night-mode');
      bodyElement.classList.add('light-theme');
      localStorage.removeItem('gmtNightMode');
      localStorage.setItem('eventox_theme', 'light');
    }
  };

  // Initialize theme on mount (prefer new key, fall back to legacy gmtNightMode)
  useEffect(() => {
    const savedTheme = localStorage.getItem('eventox_theme');
    const legacyNight = localStorage.getItem('gmtNightMode');

    let nightMode = true;
    if (savedTheme === 'light') {
      nightMode = false;
    } else if (savedTheme === 'dark') {
      nightMode = true;
    } else if (legacyNight) {
      nightMode = true;
    } else if (legacyNight === null && savedTheme === null) {
      // First visit: dark by default (mock default)
      nightMode = true;
    }

    setIsNightMode(nightMode);
    applyThemeClasses(nightMode);
  }, []);

  const toggleNightMode = () => {
    const newNightModeState = !isNightMode;
    setIsNightMode(newNightModeState);
    applyThemeClasses(newNightModeState);
  };

  return (
    <NightModeContext.Provider value={{ isNightMode, toggleNightMode }}>
      {children}
    </NightModeContext.Provider>
  );
};
